// src/pages/InsuranceLinks.jsx - DYNAMIC VERSION with API (FIXED)
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AOS from 'aos';
import 'aos/dist/aos.css';

// Import components
import TopHeader from '../components/dashboard/TopHeader/TopHeader';
import Sidebar from '../components/Global/sidebar'; // ✅ Import Reusable Sidebar
import { authenticatedFetch, clearStoredCredentials } from '../utils/api';

// Import styles
import '../styles/sidebar.css';

const API_BASE = process.env.REACT_APP_API_URL || 'https://localhost:7018';

function InsuranceLinks() {
  const navigate = useNavigate();
  const location = useLocation();
  
  // UI State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [selectedLink, setSelectedLink] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  
  // Modal State
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // ✅ API State
  const [insuranceLinks, setInsuranceLinks] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Form State
  const [generateForm, setGenerateForm] = useState({
    linkTitle: '',
    operations: [],
    productIds: [],
    purchaseCharge: 'Customer',
    renewalCharge: 'Customer',
    chargeMode: 'Disabled',
    expiryType: 'indefinite',
    customExpiry: ''
  });
  const [generateErrors, setGenerateErrors] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  
  // Edit Form State
  const [editForm, setEditForm] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const [isEditing, setIsEditing] = useState(false);

  // ✅ Session Helper
  const getSession = () => {
    try {
      return JSON.parse(localStorage.getItem('userSession') || '{}');
    } catch { return {}; }
  };
  
  const getCompanyId = () => {
    const session = getSession();
    return session.companyId || session.sessionId || null;
  };

  // ✅ Fetch Products from /Settings/products endpoint
  const fetchProducts = useCallback(async () => {
    try {
      setProductsLoading(true);
      const response = await authenticatedFetch(`${API_BASE}/Settings/products`, 'POST', {});
      
      const isSuccess = response?.success === true || response?.status === 'success';
      if (isSuccess) {
        const apiData = response.data || response.result || [];
        const formatted = apiData.map(p => ({
          id: p.id || p.Id,
          name: p.name || p.Name,
          category: p.categoryName || p.CategoryName,
          premium: p.minimumPremium ?? p.MinimumPremium
        }));
        setProducts(formatted);
        console.log('✅ Products loaded:', formatted.length);
      } else {
        throw new Error(response?.message || 'Failed to fetch products');
      }
    } catch (err) {
      console.error('💥 Products fetch error:', err);
      toast.error('Could not load products');
    } finally {
      setProductsLoading(false);
    }
  }, []);

  // ✅ Fetch Insurance Links from API - FIXED: Normalize bundledProducts
  const fetchInsuranceLinks = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const companyId = getCompanyId();
      if (!companyId) {
        toast.warning('Session expired. Please log in again.');
        navigate('/login', { replace: true });
        return;
      }
      
      const response = await authenticatedFetch(
        `${API_BASE}/Settings/insurance-links`,
        'POST',
        { companyId }
      );
      
      const isSuccess = response?.success === true || response?.status === 'success';
      if (isSuccess) {
        const apiData = response.data || response.result || [];
        
        const normalizedLinks = apiData.map(link => ({
          ...link,
          bundledProducts: (link.bundledProducts || []).map(p => ({
            id: p.id || p.Id,
            name: p.name || p.Name,
            categoryId: p.categoryId || p.CategoryId,
            categoryName: p.categoryName || p.CategoryName,
            minimumPremium: p.minimumPremium ?? p.MinimumPremium,
            status: p.status || p.Status
          }))
        }));
        
        console.log('✅ Insurance links loaded:', normalizedLinks.length);
        if (normalizedLinks.length > 0) {
          console.log('📦 First link bundled products:', normalizedLinks[0].bundledProducts);
        }
        
        setInsuranceLinks(normalizedLinks);
      } else {
        const msg = (response?.message || '').toLowerCase();
        if (msg.includes('expired') || msg.includes('unauthorized') || msg.includes('401')) {
          clearStoredCredentials();
          localStorage.removeItem('userSession');
          toast.warning('Session expired. Please log in again.');
          navigate('/login', { replace: true });
          return;
        }
        throw new Error(response?.message || 'Failed to fetch links');
      }
    } catch (err) {
      console.error('💥 Links fetch error:', err);
      const errorMsg = err.message?.toLowerCase() || '';
      if (errorMsg.includes('401') || errorMsg.includes('expired')) {
        clearStoredCredentials();
        localStorage.removeItem('userSession');
        toast.warning('Session expired. Please log in again.');
        navigate('/login', { replace: true });
      } else {
        setError(err.message || 'Could not load insurance links');
        toast.error(err.message || 'Could not load insurance links');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // ✅ Load data on mount
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
    fetchProducts();
    fetchInsuranceLinks();
  }, [fetchProducts, fetchInsuranceLinks]);

  // ✅ Handle payment success toast from CoverageDetails
  useEffect(() => {
    if (location.state?.paymentSuccess) {
      toast.success(`Payment successful! Reference: ${location.state.reference}`);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // ✅ Available options
  const availableOperations = ['Purchase', 'Renewal', 'ManagePolicy'];

  // ✅ Generate Link URL
  const generateLinkUrl = (companyId, linkId) => {
    return `${window.location.origin}/insurance/landing?company=${companyId}&link=${linkId}`;
  };

  // ✅ Copy to Clipboard
  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      toast.success('Link copied!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopiedId(id);
      toast.success('Link copied!');
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  // ✅ Modal Handlers
  const handleViewDetails = (link) => {
    console.log('🔍 Viewing link details:', link);
    console.log('📦 Bundled products:', link.bundledProducts);
    setSelectedLink(link);
    setShowDetailsModal(true);
    setActiveTab('details');
  };
  
  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setTimeout(() => setSelectedLink(null), 300);
  };
  
  const handleOpenGenerateModal = () => {
    setGenerateForm({
      linkTitle: '', operations: [], productIds: [],
      purchaseCharge: 'Customer', renewalCharge: 'Customer',
      chargeMode: 'Disabled', expiryType: 'indefinite', customExpiry: ''
    });
    setGenerateErrors({});
    setShowGenerateModal(true);
  };
  
  const handleOpenEditModal = (link) => {
    const ops = link.operations?.split(',').map(o => o.trim()).filter(Boolean) || [];
    const productIds = link.bundledProducts?.map(p => p.id) || [];
    
    setEditForm({
      id: link.id, linkTitle: link.linkTitle, operations: ops,
      productIds: productIds,
      purchaseCharge: link.purchaseCharge || 'Customer',
      renewalCharge: link.renewalCharge || 'Customer',
      chargeMode: link.chargeMode || 'Disabled',
      expiryType: link.expiryDate === 'Indefinite' ? 'indefinite' : 'custom',
      customExpiry: link.expiryDate !== 'Indefinite' ? link.expiryDate : '',
      status: link.status
    });
    setEditErrors({});
    setShowEditModal(true);
  };
  
  const handleCloseGenerateModal = () => { setShowGenerateModal(false); setGenerateErrors({}); };
  const handleCloseEditModal = () => { setShowEditModal(false); setEditErrors({}); };

  // ✅ Form Change Handlers
  const handleGenerateChange = (field, value) => {
    setGenerateForm(prev => ({ ...prev, [field]: value }));
    if (generateErrors[field]) setGenerateErrors(prev => ({ ...prev, [field]: '' }));
  };
  
  const handleGenerateToggle = (field, value) => {
    setGenerateForm(prev => {
      const current = prev[field] || [];
      return {
        ...prev,
        [field]: current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value]
      };
    });
  };
  
  const handleEditChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
    if (editErrors[field]) setEditErrors(prev => ({ ...prev, [field]: '' }));
  };
  
  const handleEditToggle = (field, value) => {
    setEditForm(prev => {
      const current = prev[field] || [];
      return {
        ...prev,
        [field]: current.includes(value)
          ? current.filter(v => v !== value)
          : [...current, value]
      };
    });
  };

  // ✅ Validation
  const validateGenerateForm = () => {
    const errors = {};
    if (!generateForm.linkTitle?.trim()) errors.linkTitle = 'Link title is required';
    if (!generateForm.operations?.length) errors.operations = 'Select at least one operation';
    if (!generateForm.productIds?.length) errors.productIds = 'Select at least one product';
    if (generateForm.expiryType === 'custom' && !generateForm.customExpiry)
      errors.customExpiry = 'Please select an expiry date';
    setGenerateErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const validateEditForm = () => {
    const errors = {};
    if (!editForm.linkTitle?.trim()) errors.linkTitle = 'Link title is required';
    if (!editForm.operations?.length) errors.operations = 'Select at least one operation';
    if (!editForm.productIds?.length) errors.productIds = 'Select at least one product';
    if (editForm.expiryType === 'custom' && !editForm.customExpiry)
      errors.customExpiry = 'Please select an expiry date';
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ✅ API: Generate/Save Link
  const handleGenerateSubmit = async (e) => {
    e.preventDefault();
    if (!validateGenerateForm()) return;
    
    try {
      setIsGenerating(true);
      const companyId = getCompanyId();
      if (!companyId) {
        toast.error('Session expired. Please log in again.');
        navigate('/login', { replace: true });
        return;
      }
      
      const payload = {
        companyId,
        linkTitle: generateForm.linkTitle.trim(),
        operations: generateForm.operations,
        productIds: generateForm.productIds,
        purchaseCharge: generateForm.purchaseCharge,
        renewalCharge: generateForm.renewalCharge,
        chargeMode: generateForm.chargeMode,
        expiryDate: generateForm.expiryType === 'indefinite' ? null : generateForm.customExpiry,
        status: 'Active'
      };
      
      const response = await authenticatedFetch(
        `${API_BASE}/Settings/create-insurance-link`,
        'POST',
        payload
      );
      
      if (response?.success === true) {
        toast.success('Link created successfully!');
        handleCloseGenerateModal();
        await fetchInsuranceLinks();
      } else {
        throw new Error(response?.message || 'Failed to create link');
      }
    } catch (err) {
      console.error('💥 Create link error:', err);
      toast.error(err.message || 'Failed to create link');
    } finally {
      setIsGenerating(false);
    }
  };

  // ✅ API: Update Link
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!validateEditForm()) return;
    
    try {
      setIsEditing(true);
      toast.success('Link updated! (Update endpoint pending)');
      handleCloseEditModal();
      await fetchInsuranceLinks();
    } catch (err) {
      console.error('💥 Update link error:', err);
      toast.error(err.message || 'Failed to update link');
    } finally {
      setIsEditing(false);
    }
  };

  // ✅ API: Delete Link
  const handleDeleteLink = async (linkId, linkTitle) => {
    if (!window.confirm(`Delete "${linkTitle}"? This action cannot be undone.`)) return;
    
    try {
      const response = await authenticatedFetch(
        `${API_BASE}/Settings/delete-insurance-link`,
        'POST',
        { id: linkId }
      );
      
      if (response?.success === true) {
        setInsuranceLinks(prev => prev.filter(l => l.id !== linkId));
        if (selectedLink?.id === linkId) handleCloseModal();
        toast.success('Link deleted successfully');
      } else {
        throw new Error(response?.message || 'Delete failed');
      }
    } catch (err) {
      console.error('💥 Delete error:', err);
      toast.error(err.message || 'Failed to delete link');
    }
  };

  // ✅ API: Toggle Status
  const handleToggleStatus = async (linkId, currentStatus) => {
    try {
      const newStatus = currentStatus === 'Active' ? 'Inactive' : 'Active';
      const response = await authenticatedFetch(
        `${API_BASE}/Settings/update-insurance-link-status`,
        'POST',
        { id: linkId, status: newStatus }
      );
      
      if (response?.success === true) {
        setInsuranceLinks(prev => prev.map(l => 
          l.id === linkId ? { ...l, status: newStatus, linkStatus: newStatus } : l
        ));
        if (selectedLink?.id === linkId) {
          setSelectedLink(prev => prev ? { ...prev, status: newStatus, linkStatus: newStatus } : null);
        }
        toast.success(`Link ${newStatus.toLowerCase()}!`);
      } else {
        throw new Error(response?.message || 'Update failed');
      }
    } catch (err) {
      console.error('💥 Toggle status error:', err);
      toast.error(err.message || 'Failed to update status');
    }
  };

  // ✅ Logout
  const handleLogout = (e) => {
    e?.preventDefault();
    clearStoredCredentials();
    localStorage.removeItem('userSession');
    toast.success('Logged out successfully');
    navigate('/login', { replace: true });
  };

  // ✅ Navigation Handler
  const handleNavClick = (path, e) => {
    e?.preventDefault();
    if (path) navigate(path);
    if (window.innerWidth < 992) setSidebarOpen(false);
  };

  // ✅ Filter & Search
  const filteredLinks = insuranceLinks.filter(link => {
    if (activeFilter === 'active') return link.status === 'Active';
    if (activeFilter === 'inactive') return link.status !== 'Active';
    return true;
  }).filter(link =>
    link.linkTitle?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    link.companyId?.toString().includes(searchTerm) ||
    link.linkId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // ✅ Stats
  const stats = {
    multiple: insuranceLinks.filter(l => l.linkType === 'Multiple').length,
    purchase: insuranceLinks.filter(l => l.operations?.includes('Purchase')).length,
    renewal: insuranceLinks.filter(l => l.operations?.includes('Renewal')).length,
    claim: 0
  };

  // ✅ Loading State
  if (loading && insuranceLinks.length === 0) {
    return (
      <div className="insurance-links-page d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-orange mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading insurance links...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="insurance-links-page">
      <ToastContainer position="top-right" autoClose={3000} theme="light" />
      
      {/* ✅ Left Sidebar (Using Reusable Component) */}
      <Sidebar 
        sidebarOpen={sidebarOpen} 
        onNavClick={handleNavClick} 
        onLogout={handleLogout} 
      />

      {/* Main Content */}
      <div className="main-content">
        <TopHeader 
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          pageTitle="HMO Links"
          pageIcon="fa-link"
        />

        <div className="content-wrapper">
          {/* Header */}
          <div className="mb-4">
            <h5 className="mb-1">Insurance links <span className="text-muted small">{insuranceLinks.length} links</span></h5>
            <small className="text-muted">Manage and generate insurance product links</small>
          </div>

          {/* Actions */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <button className="btn btn-generate" onClick={handleOpenGenerateModal}
              style={{ backgroundColor: '#F89423', color: 'white', border: 'none', padding: '10px 20px', fontWeight: '600', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <i className="fas fa-link"></i>Generate link
            </button>
            <div className="d-flex gap-3">
              <div className="stat-card" style={{ backgroundColor: '#fef3e2', padding: '12px 20px', borderRadius: '8px', textAlign: 'center', minWidth: '100px' }}>
                <div className="text-muted small mb-1">Multiple</div>
                <div className="fw-bold" style={{ color: '#F89423', fontSize: '20px' }}>{stats.multiple}</div>
              </div>
              <div className="stat-card" style={{ backgroundColor: '#f3f4f6', padding: '12px 20px', borderRadius: '8px', textAlign: 'center', minWidth: '100px' }}>
                <div className="text-muted small mb-1">Purchase</div>
                <div className="fw-bold" style={{ color: '#374151', fontSize: '20px' }}>{stats.purchase}</div>
              </div>
              <div className="stat-card" style={{ backgroundColor: '#fef3c7', padding: '12px 20px', borderRadius: '8px', textAlign: 'center', minWidth: '100px' }}>
                <div className="text-muted small mb-1">Renewal</div>
                <div className="fw-bold" style={{ color: '#f59e0b', fontSize: '20px' }}>{stats.renewal}</div>
              </div>
            </div>
          </div>

          {/* Filters & Search */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="btn-group" role="group">
              {['all', 'active', 'inactive'].map(filter => (
                <button key={filter} className={`btn ${activeFilter === filter ? 'btn-active' : 'btn-outline-secondary'}`}
                  onClick={() => setActiveFilter(filter)}
                  style={{ backgroundColor: activeFilter === filter ? '#1f2937' : 'white', color: activeFilter === filter ? 'white' : '#6b7280', border: '1px solid #d1d5db', borderLeft: filter !== 'all' ? 'none' : undefined, padding: '8px 16px', fontWeight: '500', borderRadius: filter === 'all' ? '6px 0 0 6px' : filter === 'inactive' ? '0 6px 6px 0' : undefined, cursor: 'pointer' }}>
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>
            <div className="d-flex gap-2 align-items-center">
              <div className="search-box" style={{ position: 'relative' }}>
                <i className="fas fa-search" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }}></i>
                <input type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingLeft: '40px', padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '8px', width: '250px', outline: 'none' }} />
              </div>
              <button className="btn btn-outline-success" style={{ border: '1px solid #d1d5db', padding: '8px 16px', borderRadius: '8px', color: '#F89423', backgroundColor: '#fef3e2', cursor: 'pointer' }}>
                <i className="fas fa-download me-2"></i>Export
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="table-responsive" style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            {filteredLinks.length === 0 ? (
              <div className="text-center py-5 text-muted">
                <i className="fas fa-link fa-4x mb-3 opacity-25"></i>
                <h5>No insurance links found</h5>
                <p className="mb-0">Generate your first link to get started!</p>
              </div>
            ) : (
              <table className="table table-hover mb-0">
                <thead style={{ backgroundColor: '#f9fafb' }}>
                  <tr>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>S/N</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Link Title</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Products</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Link ID</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Operations</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Status</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Date Generated</th>
                    <th className="py-3 px-4" style={{ fontWeight: '600', fontSize: '13px', color: '#374151' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLinks.map((link, index) => {
                    const linkUrl = generateLinkUrl(link.companyId, link.linkId);
                    return (
                      <tr key={link.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td className="py-3 px-4">{index + 1}</td>
                        <td className="py-3 px-4" style={{ fontWeight: '500' }}>{link.linkTitle}</td>
                        <td className="py-3 px-4">
                          <span className="badge bg-orange-light text-orange" style={{ fontSize: '12px' }}>
                            {link.bundledProducts?.length || 0} products
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <a href={linkUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#F89423', textDecoration: 'none', wordBreak: 'break-all', fontSize: '13px' }}>{link.linkId}</a>
                            <button onClick={() => copyToClipboard(linkUrl, link.id)} style={{ background: 'none', border: 'none', color: copiedId === link.id ? '#10b981' : '#6b7280', cursor: 'pointer', padding: '4px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }} title="Copy to clipboard">
                              <i className={`fas ${copiedId === link.id ? 'fa-check' : 'fa-copy'}`}></i>
                            </button>
                          </div>
                        </td>
                        <td className="py-3 px-4" style={{ fontSize: '13px' }}>{link.operations}</td>
                        <td className="py-3 px-4">
                          <span className="badge" style={{ backgroundColor: link.status === 'Active' ? '#fef3e2' : '#f3f4f6', color: link.status === 'Active' ? '#F89423' : '#6b7280', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' }}>
                            <i className="fas fa-circle me-1" style={{ fontSize: '6px' }}></i>{link.status}
                          </span>
                        </td>
                        <td className="py-3 px-4" style={{ fontSize: '13px' }}>{link.dateGenerated}</td>
                        <td className="py-3 px-4">
                          <div className="btn-group btn-group-sm">
                            <button className="btn btn-outline-primary btn-sm" onClick={() => handleOpenEditModal(link)} title="Edit Link"><i className="fas fa-edit"></i></button>
                            <button className="btn btn-outline-danger btn-sm" onClick={() => handleDeleteLink(link.id, link.linkTitle)} title="Delete Link"><i className="fas fa-trash"></i></button>
                            <button className="btn btn-link btn-sm" onClick={() => handleViewDetails(link)} style={{ color: '#F89423', padding: '4px 8px' }}>View</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* ✅ GENERATE LINK MODAL */}
      {showGenerateModal && (
        <div className="modal-overlay" onClick={handleCloseGenerateModal}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ width: '700px', maxWidth: '95%' }}>
            <div className="modal-header" style={{ padding: '20px 30px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h5 className="mb-1" style={{ color: '#1f2937' }}><i className="fas fa-link me-2"></i>Generate New Link</h5>
                <p className="text-muted small mb-0">Select products to bundle with this link</p>
              </div>
              <button onClick={handleCloseGenerateModal} style={{ background: 'none', border: 'none', fontSize: '24px', color: '#6b7280', cursor: 'pointer', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }} onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'} onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>×</button>
            </div>
            <form onSubmit={handleGenerateSubmit}>
              <div className="modal-content" style={{ padding: '25px 30px', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                {/* Link Title */}
                <div className="mb-4">
                  <label className="form-label fw-semibold small">Link Title <span className="text-danger">*</span></label>
                  <input type="text" className="form-control" value={generateForm.linkTitle} onChange={(e) => handleGenerateChange('linkTitle', e.target.value)} placeholder="e.g., Seniors Plans by CoverME" disabled={isGenerating} />
                  {generateErrors.linkTitle && <small className="text-danger">{generateErrors.linkTitle}</small>}
                </div>

                {/* Operations */}
                <div className="mb-4">
                  <label className="form-label fw-semibold small">Operations <span className="text-danger">*</span></label>
                  <div className="d-flex flex-wrap gap-2">
                    {availableOperations.map(op => (
                      <label key={op} className="form-check-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', border: `1px solid ${generateForm.operations.includes(op) ? '#F89423' : '#d1d5db'}`, borderRadius: '8px', backgroundColor: generateForm.operations.includes(op) ? '#fef3e2' : 'white', cursor: isGenerating ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: generateForm.operations.includes(op) ? '600' : '500', color: generateForm.operations.includes(op) ? '#F89423' : '#374151' }}>
                        <input type="checkbox" className="form-check-input" checked={generateForm.operations.includes(op)} onChange={() => handleGenerateToggle('operations', op)} disabled={isGenerating} style={{ accentColor: '#F89423' }} />{op}
                      </label>
                    ))}
                  </div>
                  {generateErrors.operations && <small className="text-danger d-block mt-1">{generateErrors.operations}</small>}
                </div>

                {/* Products from API */}
                <div className="mb-4">
                  <label className="form-label fw-semibold small">Bundled Products <span className="text-danger">*</span></label>
                  {productsLoading ? (
                    <div className="text-center py-3 text-muted">
                      <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                      Loading products...
                    </div>
                  ) : (
                    <div className="d-flex flex-wrap gap-2" style={{ maxHeight: '200px', overflowY: 'auto', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: '#f9fafb' }}>
                      {products.map(product => (
                        <label key={product.id} className="form-check-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', border: `1px solid ${generateForm.productIds.includes(product.id) ? '#F89423' : '#d1d5db'}`, borderRadius: '6px', backgroundColor: generateForm.productIds.includes(product.id) ? '#fef3e2' : 'white', cursor: isGenerating ? 'not-allowed' : 'pointer', fontSize: '12px', fontWeight: generateForm.productIds.includes(product.id) ? '600' : '500', color: generateForm.productIds.includes(product.id) ? '#F89423' : '#374151' }}>
                          <input type="checkbox" className="form-check-input" checked={generateForm.productIds.includes(product.id)} onChange={() => handleGenerateToggle('productIds', product.id)} disabled={isGenerating} style={{ accentColor: '#F89423', width: '14px', height: '14px' }} />
                          {product.name}
                          <small className="text-muted ms-1">({product.category})</small>
                        </label>
                      ))}
                    </div>
                  )}
                  {generateErrors.productIds && <small className="text-danger d-block mt-1">{generateErrors.productIds}</small>}
                </div>

                {/* Charge Settings */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold small">Purchase Charge</label>
                    <select className="form-select" value={generateForm.purchaseCharge} onChange={(e) => handleGenerateChange('purchaseCharge', e.target.value)} disabled={isGenerating}>
                      <option value="Customer">Customer</option>
                      <option value="Company">Company</option>
                      <option value="Shared">Shared</option>
                    </select>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold small">Renewal Charge</label>
                    <select className="form-select" value={generateForm.renewalCharge} onChange={(e) => handleGenerateChange('renewalCharge', e.target.value)} disabled={isGenerating}>
                      <option value="Customer">Customer</option>
                      <option value="Company">Company</option>
                      <option value="Shared">Shared</option>
                    </select>
                  </div>
                </div>

                {/* Charge Mode */}
                <div className="mb-4">
                  <label className="form-label fw-semibold small">Charge Mode</label>
                  <div className="btn-group" role="group">
                    {['Disabled', 'Enabled'].map(mode => (
                      <button key={mode} type="button" className={`btn ${generateForm.chargeMode === mode ? 'btn-active' : 'btn-outline-secondary'}`} onClick={() => handleGenerateChange('chargeMode', mode)} disabled={isGenerating}
                        style={{ backgroundColor: generateForm.chargeMode === mode ? '#F89423' : 'white', color: generateForm.chargeMode === mode ? 'white' : '#6b7280', border: '1px solid #d1d5db', borderLeft: mode !== 'Disabled' ? 'none' : undefined, padding: '8px 20px', fontWeight: '500', borderRadius: mode === 'Disabled' ? '6px 0 0 6px' : mode === 'Enabled' ? '0 6px 6px 0' : undefined, opacity: isGenerating ? 0.7 : 1, cursor: isGenerating ? 'not-allowed' : 'pointer' }}>{mode}</button>
                    ))}
                  </div>
                </div>

                {/* Expiry */}
                <div className="mb-4">
                  <label className="form-label fw-semibold small">Expiry Date</label>
                  <div className="d-flex align-items-center gap-3">
                    <label className="form-check-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isGenerating ? 'not-allowed' : 'pointer' }}>
                      <input type="radio" name="expiryType" checked={generateForm.expiryType === 'indefinite'} onChange={() => handleGenerateChange('expiryType', 'indefinite')} disabled={isGenerating} style={{ accentColor: '#F89423' }} /><span style={{ fontSize: '14px', fontWeight: '500' }}>Indefinite</span>
                    </label>
                    <label className="form-check-label" style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: isGenerating ? 'not-allowed' : 'pointer' }}>
                      <input type="radio" name="expiryType" checked={generateForm.expiryType === 'custom'} onChange={() => handleGenerateChange('expiryType', 'custom')} disabled={isGenerating} style={{ accentColor: '#F89423' }} /><span style={{ fontSize: '14px', fontWeight: '500' }}>Custom</span>
                    </label>
                    {generateForm.expiryType === 'custom' && <input type="date" className="form-control" style={{ width: 'auto', minWidth: '180px' }} value={generateForm.customExpiry} onChange={(e) => handleGenerateChange('customExpiry', e.target.value)} disabled={isGenerating} min={new Date().toISOString().split('T')[0]} />}
                  </div>
                  {generateErrors.customExpiry && <small className="text-danger d-block mt-1">{generateErrors.customExpiry}</small>}
                </div>
              </div>
              <div className="modal-footer" style={{ padding: '15px 30px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" className="btn btn-secondary" onClick={handleCloseGenerateModal} disabled={isGenerating}>Cancel</button>
                <button type="submit" className="btn" disabled={isGenerating} style={{ backgroundColor: '#F89423', color: 'white', border: 'none', padding: '10px 24px', fontWeight: '600', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {isGenerating ? <><span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>Creating...</> : <><i className="fas fa-link"></i>Generate Link</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ✅ EDIT MODAL */}
      {showEditModal && (
        <div className="modal-overlay" onClick={handleCloseEditModal}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()} style={{ width: '700px', maxWidth: '95%' }}>
            <div className="modal-header" style={{ padding: '20px 30px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div><h5 className="mb-1" style={{ color: '#1f2937' }}><i className="fas fa-edit me-2"></i>Edit Link</h5></div>
              <button onClick={handleCloseEditModal} style={{ background: 'none', border: 'none', fontSize: '24px', color: '#6b7280', cursor: 'pointer', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' }}>×</button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="modal-content" style={{ padding: '25px 30px', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)' }}>
                <div className="mb-4">
                  <label className="form-label fw-semibold small">Link Title <span className="text-danger">*</span></label>
                  <input type="text" className="form-control" value={editForm.linkTitle || ''} onChange={(e) => handleEditChange('linkTitle', e.target.value)} disabled={isEditing} />
                  {editErrors.linkTitle && <small className="text-danger">{editErrors.linkTitle}</small>}
                </div>
              </div>
              <div className="modal-footer" style={{ padding: '15px 30px', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button type="button" className="btn btn-secondary" onClick={handleCloseEditModal} disabled={isEditing}>Cancel</button>
                <button type="submit" className="btn" disabled={isEditing} style={{ backgroundColor: '#F89423', color: 'white', border: 'none', padding: '10px 24px', fontWeight: '600', borderRadius: '8px' }}>
                  {isEditing ? <><span className="spinner-border spinner-border-sm"></span>Saving...</> : <><i className="fas fa-save"></i>Save Changes</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ✅ ENHANCED DETAILS MODAL with Tabs - FIXED: Products Display */}
      {showDetailsModal && selectedLink && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-panel" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="modal-header" style={{ padding: '20px 30px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h5 className="mb-1" style={{ color: '#1f2937' }}>Link details</h5>
                <p className="text-muted small mb-0">{selectedLink.linkTitle}</p>
              </div>
              <button onClick={handleCloseModal} style={{ background: 'none', border: 'none', fontSize: '24px', color: '#6b7280', cursor: 'pointer', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', transition: 'all 0.2s ease' }} onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'} onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}>×</button>
            </div>

            {/* Modal Tabs */}
            <div className="modal-tabs" style={{ padding: '0 30px', borderBottom: '1px solid #e5e7eb', display: 'flex', gap: '30px' }}>
              <button onClick={() => setActiveTab('details')} style={{ padding: '15px 0', background: 'none', border: 'none', borderBottom: activeTab === 'details' ? '2px solid #F89423' : '2px solid transparent', color: activeTab === 'details' ? '#F89423' : '#6b7280', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}>Link details</button>
              <button onClick={() => setActiveTab('history')} style={{ padding: '15px 0', background: 'none', border: 'none', borderBottom: activeTab === 'history' ? '2px solid #F89423' : '2px solid transparent', color: activeTab === 'history' ? '#F89423' : '#6b7280', fontWeight: '600', cursor: 'pointer', fontSize: '14px' }}>Usage history</button>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: '10px' }}>
                <button onClick={() => handleOpenEditModal(selectedLink)} style={{ padding: '8px 16px', background: 'none', border: 'none', color: '#10b981', cursor: 'pointer', fontWeight: '500', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}><i className="fas fa-edit"></i> Edit link</button>
                <button onClick={() => handleDeleteLink(selectedLink.id, selectedLink.linkTitle)} style={{ padding: '8px 16px', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: '500', fontSize: '13px' }}>Delete link</button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="modal-content" style={{ padding: '30px', overflowY: 'auto', maxHeight: 'calc(100vh - 250px)' }}>
              {activeTab === 'details' ? (
                <div>
                  {/* Info Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '30px', marginBottom: '30px' }}>
                    <div><div className="text-muted small mb-1">Link type</div><div className="fw-semibold" style={{ fontSize: '14px' }}>{selectedLink.linkType || 'N/A'}</div></div>
                    <div><div className="text-muted small mb-1">Link title</div><div className="fw-semibold" style={{ fontSize: '14px' }}>{selectedLink.linkTitle}</div></div>
                    <div><div className="text-muted small mb-1">Status</div><span className="badge" style={{ backgroundColor: selectedLink.linkStatus === 'Active' ? '#d1fae5' : '#f3f4f6', color: selectedLink.linkStatus === 'Active' ? '#10b981' : '#6b7280', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' }}>{selectedLink.linkStatus || selectedLink.status}</span></div>
                    <div><div className="text-muted small mb-1">Purchase charge</div><div className="fw-semibold" style={{ fontSize: '14px' }}>{selectedLink.purchaseCharge || 'N/A'}</div></div>
                    <div><div className="text-muted small mb-1">Renewal charge</div><div className="fw-semibold" style={{ fontSize: '14px' }}>{selectedLink.renewalCharge || 'N/A'}</div></div>
                    <div><div className="text-muted small mb-1">Date generated</div><div className="fw-semibold" style={{ fontSize: '14px' }}>{selectedLink.dateGenerated}</div></div>
                    <div><div className="text-muted small mb-1">Activation date</div><div className="fw-semibold" style={{ fontSize: '14px' }}>{selectedLink.activationDate || 'N/A'}</div></div>
                    <div><div className="text-muted small mb-1">Expiry date</div><div className="fw-semibold" style={{ fontSize: '14px' }}>{selectedLink.expiryDate || 'Indefinite'}</div></div>
                    <div><div className="text-muted small mb-1">Link URL</div><a href={generateLinkUrl(selectedLink.companyId, selectedLink.linkId)} target="_blank" rel="noopener noreferrer" style={{ color: '#F89423', textDecoration: 'none', fontSize: '13px', wordBreak: 'break-all' }}>{selectedLink.linkId}</a></div>
                  </div>

                  {/* Status Toggle */}
                  <div style={{ marginBottom: '30px' }}>
                    <div className="text-muted small mb-2">Link status</div>
                    <div className="btn-group" role="group">
                      <button className="btn" onClick={() => handleToggleStatus(selectedLink.id, selectedLink.status)} style={{ backgroundColor: selectedLink.status === 'Active' ? '#10b981' : '#f3f4f6', color: selectedLink.status === 'Active' ? 'white' : '#6b7280', border: 'none', padding: '8px 20px', fontWeight: '600', borderRadius: '6px 0 0 6px', cursor: 'pointer' }}>Enabled</button>
                      <button className="btn" onClick={() => handleToggleStatus(selectedLink.id, selectedLink.status)} style={{ backgroundColor: selectedLink.status !== 'Active' ? '#6b7280' : '#f3f4f6', color: selectedLink.status !== 'Active' ? 'white' : '#6b7280', border: 'none', padding: '8px 20px', fontWeight: '600', borderRadius: '0 6px 6px 0', cursor: 'pointer' }}>Disable</button>
                    </div>
                  </div>

                  {/* Operations */}
                  <div style={{ marginBottom: '30px' }}>
                    <h6 className="mb-3" style={{ color: '#1f2937' }}>Operations</h6>
                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                      {selectedLink.operations?.split(',').map((op, idx) => (
                        <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', backgroundColor: '#f3f4f6', borderRadius: '8px' }}><i className="fas fa-check-circle" style={{ color: '#10b981' }}></i><span style={{ fontWeight: '500', fontSize: '14px' }}>{op.trim()}</span></div>
                      ))}
                    </div>
                  </div>

                  {/* ✅ FIXED: Bundled Products - Handle both PascalCase and camelCase */}
                  <div>
                    <h6 className="mb-3" style={{ color: '#1f2937' }}>Bundled products ({selectedLink.bundledProducts?.length || 0})</h6>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {(selectedLink.bundledProducts || []).map((product, idx) => {
                        const productName = product.name || product.Name || 'Unknown Product';
                        const productPremium = product.minimumPremium ?? product.MinimumPremium ?? 0;
                        const productCategory = product.categoryName || product.CategoryName || '';
                        
                        return (
                          <span 
                            key={product.id || product.Id || idx} 
                            style={{ 
                              display: 'inline-block', 
                              padding: '8px 16px', 
                              backgroundColor: '#f3f4f6', 
                              borderRadius: '8px', 
                              fontSize: '13px',
                              color: '#374151',
                              fontWeight: '500',
                              width: 'fit-content'
                            }}
                          >
                            {productName}
                            {productCategory && <span className="text-muted ms-1">({productCategory})</span>}
                            {productPremium > 0 && <span className="ms-2">- ₦{Number(productPremium).toLocaleString()}</span>}
                          </span>
                        );
                      })}
                      {(!selectedLink.bundledProducts || selectedLink.bundledProducts.length === 0) && (
                        <p className="text-muted small">No products bundled with this link.</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  {/* Usage History Table Header */}
                  <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr 1fr 1fr', gap: '20px', padding: '12px 20px', backgroundColor: '#f9fafb', borderRadius: '8px 8px 0 0', border: '1px solid #e5e7eb', borderBottom: 'none', fontWeight: '600', fontSize: '13px', color: '#374151' }}>
                    <div>S/N</div><div>Customer Name</div><div>Actions</div><div>Product/Policy Name</div>
                  </div>
                  {/* Usage History Table Body */}
                  <div style={{ border: '1px solid #e5e7eb', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
                    <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                      <div style={{
                        width: '64px',
                        height: '64px',
                        backgroundColor: '#d1fae5',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 20px'
                      }}>
                        <i className="fas fa-search" style={{ color: '#10b981', fontSize: '24px' }}></i>
                      </div>
                      <h6 style={{ color: '#1f2937', marginBottom: '8px', fontWeight: '600' }}>No usage history</h6>
                      <p style={{ color: '#6b7280', fontSize: '14px', margin: 0 }}>
                        This link has not been used yet. Share it with customers to start tracking usage.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* CSS Styles (Sidebar styles removed since they are in Sidebar.css now) */}
      <style>{`
        .insurance-links-page { display: flex; min-height: 100vh; background-color: #f8f9fa; }
        .modal-overlay { position: fixed; top: 0; right: 0; width: 100%; height: 100%; background-color: rgba(0, 0, 0, 0.5); z-index: 2000; display: flex; justify-content: flex-end; animation: fadeIn 0.3s ease; }
        .modal-panel { width: 900px; max-width: 90%; background: white; height: 100vh; overflow-y: auto; animation: slideInRight 0.3s ease; box-shadow: -4px 0 12px rgba(0,0,0,0.15); }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
        
        .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; }
        .form-label { display: block; margin-bottom: 6px; font-weight: 600; color: #374151; font-size: 14px; }
        .form-control, .form-select { padding: 10px 14px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; width: 100%; }
        .form-control:focus, .form-select:focus { border-color: #F89423; box-shadow: 0 0 0 3px rgba(248, 148, 35, 0.15); outline: none; }
        .form-check-input { accent-color: #F89423; }
        
        @media (max-width: 992px) { 
          .main-content { margin-left: 0; } 
        }
      `}</style>
    </div>
  );
}

export default InsuranceLinks;